import { Injectable, Type } from '@angular/core';
import { Field, FieldAttribute } from '../../../interfaces/form-and-field.interface';
import { AuthenticatorBindingFieldComponent } from '../components/authenticator-binding-field/authenticator-binding-field.component';
import { CheckboxSwitchFormFieldComponent } from '../components/checkbox-switch-form-field/checkbox-switch-form-field.component';
import { DatePickerFormFieldComponent } from '../components/date-picker-form-field/date-picker-form-field.component';
import { DocumentFormFieldComponent } from '../components/files/document-form-field/document-form-field.component';
import { PhotoFormFieldComponent } from '../components/files/photo-form-field/photo-form-field.component';
import { InputFormFieldComponent } from '../components/input-form-field/input-form-field.component';
import { LabelFormFieldComponent } from '../components/label-form-field/label-form-field.component';
import { ExtendLocationFormFieldComponent } from '../components/location-form-field/extend-location-form-field.component';
import { StringLocationFormFieldComponent } from '../components/location-form-field/string-location-form-field.component';
import { MultipleComplexSearchFormFieldComponent } from '../components/multiple-complex-search-form-field/multiple-complex-search-form-field.component';
import { MultipleCustomFormFieldComponent } from '../components/multiple-custom-names-form-field/multiple-custom-form-field.component';
import { MultipleSearchFormFieldComponent } from '../components/multiple-search-form-field/multiple-search-form-field.component';
import {
  CharityFocusesSelectTreeComponent,
  DiseasesSelectTreeComponent,
  SpecialtiesSelectTreeComponent,
} from '../components/multiple-search-form-tree/multiple-search-form-tree.component';
import { MultipleSearchMapFormFieldComponent } from '../components/multiple-search-map-form-field/multiple-search-map-form-field.component';
import { PhoneFormFieldComponent } from '../components/phone-form-field/phone-form-field.component';
import { RichEditorFieldComponent } from '../components/rich-editor-field/rich-editor-field.component';
import { SelectFormFieldComponent } from '../components/select-form-field/select-form-field.component';
import { SingleDropdownFormFieldComponent } from '../components/single-dropdown-form-field/single-dropdown-form-field.component';
import { TextareaFormFieldComponent } from '../components/textarea-form-field/textarea-form-field.component';
import { ViewportFormFieldComponent } from '../components/viewport-form-field/viewport-form-field.component';
import FieldAllowedValueClasses from '../field-allowed-value-classes.values';

@Injectable()
export class AllowedFormFieldsService {
  private readonly components: { [type: string]: Type<Field> } = {
    [FieldAllowedValueClasses.EMAIL]: InputFormFieldComponent,
    [FieldAllowedValueClasses.VERIFIED_EMAIL]: InputFormFieldComponent,
    [FieldAllowedValueClasses.WARNING_LABLE]: LabelFormFieldComponent,
    [FieldAllowedValueClasses.ALIAS]: InputFormFieldComponent,
    [FieldAllowedValueClasses.ORGANIZATION_NAME]: InputFormFieldComponent,
    [FieldAllowedValueClasses.ALPHABETIC]: InputFormFieldComponent,
    [FieldAllowedValueClasses.ALPHABETIC_NAME]: InputFormFieldComponent,
    [FieldAllowedValueClasses.CHARACTERS]: InputFormFieldComponent,
    [FieldAllowedValueClasses.ZIP_CODE]: InputFormFieldComponent,
    [FieldAllowedValueClasses.ALPHANUMERIC]: InputFormFieldComponent,
    [FieldAllowedValueClasses.SWITCH]: CheckboxSwitchFormFieldComponent,
    [FieldAllowedValueClasses.CHECKBOX]: CheckboxSwitchFormFieldComponent,
    [FieldAllowedValueClasses.TERMS_CHECKBOX]: CheckboxSwitchFormFieldComponent,
    [FieldAllowedValueClasses.URL]: InputFormFieldComponent,
    [FieldAllowedValueClasses.PHONE]: PhoneFormFieldComponent,
    [FieldAllowedValueClasses.PHONE_2FA]: PhoneFormFieldComponent,
    [FieldAllowedValueClasses.AUTHENTICATOR_BINDING]: AuthenticatorBindingFieldComponent,
    [FieldAllowedValueClasses.YES_NO_SELECT]: SelectFormFieldComponent,
    [FieldAllowedValueClasses.GENDER_SELECT]: SingleDropdownFormFieldComponent,
    [FieldAllowedValueClasses.SUFFIX_SELECT]: SelectFormFieldComponent,
    [FieldAllowedValueClasses.SEARCH_SELECT]: SelectFormFieldComponent,
    [FieldAllowedValueClasses.DATE]: DatePickerFormFieldComponent,
    [FieldAllowedValueClasses.FILE]: DocumentFormFieldComponent,
    [FieldAllowedValueClasses.MULTIPLE_FILES]: DocumentFormFieldComponent,
    [FieldAllowedValueClasses.PHOTO]: PhotoFormFieldComponent,
    [FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT]: SingleDropdownFormFieldComponent,
    [FieldAllowedValueClasses.SPECIALTIES_SELECT]: SpecialtiesSelectTreeComponent,
    [FieldAllowedValueClasses.MAP_SPECIALTIES_SELECT]: SpecialtiesSelectTreeComponent,
    [FieldAllowedValueClasses.MAP_CHARITY_FOCUSES_SELECT]: CharityFocusesSelectTreeComponent,
    [FieldAllowedValueClasses.LINKED_ORGANIZATION_IDS_SELECT]: MultipleComplexSearchFormFieldComponent,
    [FieldAllowedValueClasses.LINKED_ORGANIZATION_IDS_SELECT]: MultipleComplexSearchFormFieldComponent,
    [FieldAllowedValueClasses.LINKED_COMMUNITY_IDS_SELECT]: MultipleSearchFormFieldComponent,
    [FieldAllowedValueClasses.LINKED_PROJECT_IDS_SELECT]: MultipleSearchFormFieldComponent,
    [FieldAllowedValueClasses.ALTERNATIVE_NAME]: MultipleCustomFormFieldComponent,
    [FieldAllowedValueClasses.DISEASES_SELECT]: DiseasesSelectTreeComponent,
    [FieldAllowedValueClasses.CHARITY_FOCUSES]: CharityFocusesSelectTreeComponent,
    [FieldAllowedValueClasses.MAP_DISEASES_SELECT]: DiseasesSelectTreeComponent,
    [FieldAllowedValueClasses.LANGUAGES_SELECT]: MultipleSearchFormFieldComponent,
    [FieldAllowedValueClasses.TEXTAREA]: TextareaFormFieldComponent,
    [FieldAllowedValueClasses.TEXTAREA_FULL]: TextareaFormFieldComponent,
    [FieldAllowedValueClasses.RICH_EDITOR]: RichEditorFieldComponent,
    [FieldAllowedValueClasses.IS_OPEN_SELECT]: SelectFormFieldComponent,
    [FieldAllowedValueClasses.COMMUNITIES_SEARCH_SELECT]: SingleDropdownFormFieldComponent,
    [FieldAllowedValueClasses.CONNECT_COMMUNITY_SEARCH_SELECT]: SingleDropdownFormFieldComponent,
    [FieldAllowedValueClasses.PROJECTS_SEARCH_SELECT]: SingleDropdownFormFieldComponent,
    [FieldAllowedValueClasses.LOCATION]: ExtendLocationFormFieldComponent,
    [FieldAllowedValueClasses.CITY_LEVEL_LOCATION]: ExtendLocationFormFieldComponent,
    [FieldAllowedValueClasses.LOCATION_ALL_TYPES]: ExtendLocationFormFieldComponent,
    [FieldAllowedValueClasses.FULL_ADDRESS_LOCATION]: ExtendLocationFormFieldComponent,
    [FieldAllowedValueClasses.TEXT_LOCATION]: StringLocationFormFieldComponent,
    [FieldAllowedValueClasses.VIEWPORT]: ViewportFormFieldComponent,
    [FieldAllowedValueClasses.ROLES_SELECT]: MultipleSearchFormFieldComponent,
    [FieldAllowedValueClasses.MAP_ROLES_SELECT]: MultipleSearchMapFormFieldComponent,
  };

  public isNotAllowedField(attribute: FieldAttribute): boolean {
    return !this.components[attribute.backendAttribute.value_class_front];
  }

  public getComponent(attribute: FieldAttribute): Type<Field> {
    return this.components[attribute.backendAttribute.value_class_front];
  }
}
